//Node Modules
import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useRecoilValue, useRecoilValueLoadable } from "recoil";

//BinaryForge Components
import { PillItem, Note } from "../helpers";

//3rd Party Components
import { ProgressSpinner } from "primereact/progressspinner";

//Atoms
import { deviceSensorAlarmListAtom } from "../../atoms/alarm";
import { devicesStateSelector } from "../../atoms/dashboard";

//Helpers
import { useDeviceOfflineState } from "../../helpers/useDeviceOfflineState";

//Other
import { nav } from "../../config/navigation";

const parseZoneBreadcrumb = (value) => {
	if (!value) return null;
	if (typeof value === "object") return value;
	try {
		return JSON.parse(value);
	} catch {
		return null;
	}
};

//Zones with active alarms and/or offline devices - the "where is the problem" view
const EstateStatusCard = () => {
	const { t } = useTranslation();
	const activeAlarms = useRecoilValue(deviceSensorAlarmListAtom);
	const { state, contents: devices } = useRecoilValueLoadable(devicesStateSelector);
	const getOfflineState = useDeviceOfflineState();

	if (state === "loading")
		return (
			<div className="flex jContent-center">
				<ProgressSpinner strokeWidth="4" style={{ width: "48px", height: "48px" }} />
			</div>
		);
	if (state === "hasError")
		return (
			<Note
				messageKey={`Error: ${devices?.message ?? devices}`}
				icon="pi-exclamation-circle fontColour-error"
			/>
		);

	const zones = new Map();
	const getZoneEntry = (id, label) => {
		const key = id ?? "none";
		if (!zones.has(key)) zones.set(key, { id, label, alarmCount: 0, offlineCount: 0 });
		return zones.get(key);
	};

	//Alarmed zones from each alarm's breadcrumb leaf (the device's own zone)
	(activeAlarms ?? []).forEach((alarm) => {
		const breadcrumb = parseZoneBreadcrumb(alarm?.device?.zoneBreadcrumb);
		const ids = breadcrumb?.key?.split("|").filter(Boolean) ?? [];
		const labels = breadcrumb?.label?.split("|").filter(Boolean) ?? [];
		if (ids.length > 0) {
			getZoneEntry(ids[ids.length - 1], labels[labels.length - 1] ?? alarm?.device?.zone?.label).alarmCount += 1;
		} else {
			getZoneEntry(null, t("device.state.noLocation")).alarmCount += 1;
		}
	});

	//Offline devices grouped by zone
	devices.forEach((device) => {
		if (!getOfflineState(device).isOffline) return;
		getZoneEntry(device.zone?.id ?? null, device.zone?.label ?? t("device.state.noLocation")).offlineCount += 1;
	});

	const rows = [...zones.values()].sort(
		(a, b) => b.alarmCount - a.alarmCount || b.offlineCount - a.offlineCount,
	);

	if (rows.length === 0) {
		return (
			<div className="flex aItems-center gap-small">
				<i className="pi pi-check-circle fontColour-success" />
				<span>{t("dashboard.estate.allClear")}</span>
			</div>
		);
	}

	return (
		<div className="zoneDeviceGrid">
			{rows.map((zone) => (
				<React.Fragment key={zone.id ?? "none"}>
					{zone.id ? (
						<Link to={`${nav.zones.base}/${nav.zones.management}?zoneId=${zone.id}`} title={zone.label}>
							{zone.label}
						</Link>
					) : (
						<span title={zone.label}>{zone.label}</span>
					)}
					<div>
						{zone.alarmCount > 0 && (
							<PillItem
								type="text"
								value={t("dashboard.estate.alarmCount", { count: zone.alarmCount })}
								wrapperStyle="error"
							/>
						)}
					</div>
					<div>
						{zone.offlineCount > 0 && (
							<PillItem
								type="text"
								value={t("dashboard.estate.offlineCount", { count: zone.offlineCount })}
								wrapperStyle="grey"
							/>
						)}
					</div>
				</React.Fragment>
			))}
		</div>
	);
};

export default EstateStatusCard;
